import { defineNuxtPlugin, useRuntimeConfig } from '#imports'

// Plugin (not a composable) because this is an app-bootstrap concern: needs to run exactly once
// per session, must wire window-level intent listeners before any route change, and has no
// component lifecycle to hang off. Previously inlined into app.vue via useHead({ script })
// which ran eagerly during head parse and pre-LCP.
export default defineNuxtPlugin((nuxtApp) => {
    const { public: publicConfig } = useRuntimeConfig()
    const i18n = nuxtApp.$i18n as { locale: { value: string } } | undefined
    const locale = i18n?.locale.value
    const tokens = publicConfig.dixaTokens as Record<string, string> | undefined
    const dixaToken = locale ? tokens?.[locale]?.trim() : undefined
    if (!dixaToken) return

    if (!window._dixa_) {
        window._dixa_ = {
            invoke (method, args) {
                window._dixa_!.pendingCalls = window._dixa_!.pendingCalls || []
                window._dixa_!.pendingCalls.push([method, args])
            },
            addListener (event, listener) {
                window._dixa_!.pendingAddListenerCalls = window._dixa_!.pendingAddListenerCalls || []
                window._dixa_!.pendingAddListenerCalls.push([event, listener])
            }
        }
    }
    window._dixa_.invoke('init', { messengerToken: dixaToken })

    let loaded = false
    const load = () => {
        if (loaded) return
        loaded = true
        cleanup()
        const s = document.createElement('script')
        s.type = 'text/javascript'
        s.async = true
        s.src = 'https://messenger.dixa.io/bootstrap.js'
        const before = document.getElementsByTagName('script')[0]
        before?.parentNode?.insertBefore(s, before)
    }

    // Deferral strategy: Dixa is a chat widget that most visitors never engage with — loading it
    // for everyone wastes ~350 kB and main-thread time. Gate the bootstrap on real intent signals
    // (scroll past the fold, any pointer/key interaction) with a 5 s fallback so the bubble still
    // appears for passive readers and crawlers.
    let scrolled = false
    const onScroll = () => {
        if (scrolled || window.scrollY < 200) return
        scrolled = true
        load()
    }
    const cleanup = () => {
        window.removeEventListener('scroll', onScroll)
        window.removeEventListener('pointerdown', load)
        window.removeEventListener('keydown', load)
        if (timer != null) clearTimeout(timer)
    }

    const timer = window.setTimeout(load, 5000)
    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('pointerdown', load, { passive: true, once: true })
    window.addEventListener('keydown', load, { once: true })
})
